/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_UI_SPLIT_FINAL
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Further modularized UsersTable.tsx by extracting desktop view.
 * 
 * File Size Check:
 * - Current: 214 lines
 * - Adding: ~110 lines
 * - Projected: ~110 lines
 * - Decision: SPLIT COMPLETED
 */

import * as React from "react";
import { Spinner, Alert, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/free-solid-svg-icons";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { AdminUser } from "../../../interfaces/adminUsers";
import { formatDateTime } from "../../../utils/dateFormat";
import { useUserCountry } from "../../../hooks/useUserCountry";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { MobileUsersList } from "./components/MobileUsersList";
import { UsersTablePagination } from "./components/UsersTablePagination";
import { DesktopUsersTable } from "./components/DesktopUsersTable";
import AdminButton from "../../../components/admin/AdminButton";
import styles from "../AdminUsers.module.scss";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_USERS_TABLE
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replace raw bootstrap `btn` usages with AdminButton so retry actions use
 * the approved design-system tokens.
 *
 * File Size Check:
 * - Current: 105
 * - Adding: ~8
 * - Projected: ~113
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-danger-figma)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/users/UsersTable.tsx
 */

interface UsersTableProps {
  data: AdminUser[];
  total: number;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  loading: boolean;
  error: boolean;
  onSort: (sortBy: string, sortOrder: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  selected: string[];
  onSelectChange: (selected: string[]) => void;
  onEdit: (id: string) => void;
  onDelete?: (id: string) => void;
  onImpersonate?: (id: string) => void;
  onPreviewWorkingData?: (userId: string, userName: string, userEmail?: string) => void;
  selectedUserForCleanup?: string | null;
}

export default function UsersTable({
  data, total, page, limit, sortBy, sortOrder, loading, error,
  onSort, onPageChange, onLimitChange, selected, onSelectChange, onEdit, onDelete, onImpersonate, onPreviewWorkingData,
  selectedUserForCleanup,
}: UsersTableProps) {
  // [2026-02-13] Maya: Use useMediaQuery hook for consistent breakpoints across the app
  const { isMobile, isTablet } = useMediaQuery();
  const showMobileView = isMobile || isTablet;
  const country = useUserCountry();

  const handleSelectAll = (checked: boolean) => onSelectChange(checked ? data.map((u) => u._id) : []);
  const handleRowSelect = (id: string, checked: boolean) => onSelectChange(checked ? [...selected, id] : selected.filter((s) => s !== id));
  const handleSort = (field: string) => onSort(field, sortBy === field && sortOrder === "asc" ? "desc" : "asc");
  const getSortIcon = (field: string) => {
    if (sortBy !== field) return faSort;
    return sortOrder === "asc" ? faSortUp : faSortDown;
  };
  const formatDate = (dateString?: string) => dateString ? formatDateTime(dateString, country) : "Never";

  if (loading) return <Card><Card.Body className="text-center py-5"><Spinner animation="border" /><p className="text-muted mt-2">Loading users...</p></Card.Body></Card>;
  if (error) return <Alert variant="danger" className="text-center"><Alert.Heading>Error Loading Users</Alert.Heading><AdminButton intent="danger" size="sm" onClick={() => window.location.reload()}>Retry</AdminButton></Alert>;
  if (data.length === 0) return <Card><Card.Body className="text-center py-5"><FontAwesomeIcon icon={faSort} size="3x" className="text-muted mb-3" /><h5 className="text-muted">No users found</h5></Card.Body></Card>;

  return (
    <div className={styles['admin-users-wrapper']} style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      {showMobileView ? (
        <MobileUsersList 
          data={data} 
          selected={selected} 
          onRowSelect={handleRowSelect}
          onEdit={onEdit}
          onImpersonate={onImpersonate}
          onPreviewWorkingData={onPreviewWorkingData}
          selectedUserForCleanup={selectedUserForCleanup}
          total={total}
          page={page} 
          limit={limit} 
          onPageChange={onPageChange} 
          onLimitChange={onLimitChange} 
          formatDate={formatDate} 
        />
      ) : (
        <DesktopUsersTable 
          data={data} 
          selected={selected} 
          handleSelectAll={handleSelectAll} 
          handleRowSelect={handleRowSelect} 
          handleSort={handleSort} 
          getSortIcon={getSortIcon}
          formatDate={formatDate}
          onEdit={onEdit}
          onDelete={onDelete}
          onImpersonate={onImpersonate}
          onPreviewWorkingData={onPreviewWorkingData}
          selectedUserForCleanup={selectedUserForCleanup}
        />
      )}
      <UsersTablePagination total={total} page={page} limit={limit} onPageChange={onPageChange} onLimitChange={onLimitChange} />
    </div>
  );
}
